import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MoreHorizontal,
  X,
  ChevronRight,
  ChevronDown,
  LogOut,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { usePortalProfile } from 'contexts/PortalProfileContext';
import OrganizationSwitcher from './OrganizationSwitcher';
import { navPrincipal } from './navVisibility';
import { useFeatureAccess } from 'hooks/useFeatureAccess';
import { useFeatureGrants } from 'hooks/useFeatureGrants';
import { useSoftphoneEntitlement } from 'hooks/useSoftphoneEntitlement';
// The tree, the gating and the tab list all come from navConfig.ts, which the
// desktop sidebar reads too. This file used to hold a second copy of the tree
// and it had drifted 35 destinations behind — see the note at the top of
// navConfig.ts. What is decided HERE is only how a phone draws it: a bottom bar
// of five tabs, and a drawer for everything else.
import {
  MenuItem,
  UTILITY_ITEMS,
  activeNavPath,
  buildNavZones,
  filterNavItems,
  mobileTabItems,
} from './navConfig';

const MobileBottomNav: React.FC = () => {
  const { user, currentOrganization, logout } = useAuth();
  const { isLocked } = usePortalProfile();
  const features = useFeatureAccess();
  const grants = useFeatureGrants();
  const softphone = useSoftphoneEntitlement();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    agents: true,
    conversations: true,
  });

  // Same gate as the desktop sidebar — role AND organisation type, feature
  // locks, and the reseller's allowance. Identical call, identical result: this
  // component does no filtering of its own.
  const principal = navPrincipal(currentOrganization, user, features, grants, softphone?.allowed);
  const visibleZones = buildNavZones(principal, isLocked);
  const visibleTabs = mobileTabItems(principal, isLocked);
  const visibleUtilityItems = filterNavItems(UTILITY_ITEMS, principal, isLocked);

  // Close menu on route change
  useEffect(() => {
    setIsMoreMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMoreMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMoreMenuOpen]);

  const activePath = activeNavPath(location.pathname);
  const isActive = (path: string) => path === activePath;

  const isPathInSection = (sectionId: string) => {
    const section = visibleZones
      .flatMap((zone) => zone.sections)
      .find((sec) => sec.id === sectionId);
    return !!section?.items.some((item) => item.path === activePath);
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMoreMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMoreMenuOpen(false);
  };

  // A full-width drawer row — used for utility entries and for the items inside
  // an expanded section.
  const renderRow = (item: MenuItem, compact: boolean) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <button
        key={item.path}
        onClick={() => !item.badge && handleNavigation(item.path)}
        disabled={!!item.badge}
        className={`w-full flex items-center gap-3 rounded-xl transition-all active:scale-[0.98] ${
          compact ? 'px-3 py-2.5 rounded-lg text-sm' : 'px-4 py-3'
        } ${
          active
            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
        } ${item.badge ? 'opacity-50' : ''}`}
      >
        <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        <span className={`flex-1 text-left ${compact ? '' : 'font-medium text-sm'}`}>{item.label}</span>
        {item.badge ? (
          <span className="text-[10px] bg-yellow-500 text-white px-1.5 py-0.5 rounded font-medium">
            {item.badge}
          </span>
        ) : (
          !compact && <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
    );
  };

  return (
    <>
      {/* More Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity duration-300 ${
          isMoreMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMoreMenuOpen(false)}
      />

      {/* More Menu Panel */}
      <div
        className={`fixed inset-x-0 bottom-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl z-50 lg:hidden transition-transform duration-300 ease-out ${
          isMoreMenuOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ maxHeight: '90vh' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-2 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Menu</h3>
          <button
            onClick={() => setIsMoreMenuOpen(false)}
            className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto overscroll-contain pb-8" style={{ maxHeight: 'calc(90vh - 70px)' }}>

          {/* Which organisation you are in, above the menu it decides.
              Everything below this line — which zones appear, which sections,
              which items — is filtered by the current organisation, and every
              request carries it in X-Organization-Id. It was desktop-only,
              sitting in the sidebar footer, so a provider on a phone was stuck
              in whichever organisation they logged in to. Renders nothing for
              someone who only belongs to one. */}
          <div className="px-4 pt-3 empty:hidden">
            <OrganizationSwitcher />
          </div>

          {/* Zones — the same three the sidebar shows, in the same order, each
              dropped entirely when this person can see nothing in it. */}
          {visibleZones.map((zone) => (
            <div key={zone.id} className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                {zone.label}
              </p>

              {/* Zone-level entries, as tiles — Overview, Audit Logs, Revenue */}
              {zone.items.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {zone.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => !item.badge && handleNavigation(item.path)}
                        disabled={!!item.badge}
                        className={`flex flex-col items-center gap-1 p-3 rounded-2xl transition-all active:scale-95 ${
                          active
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                            : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300'
                        } ${item.badge ? 'opacity-50' : ''}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Sections, expandable like the sidebar's */}
              <div className="space-y-1">
                {zone.sections.map((section) => {
                  const SectionIcon = section.icon;
                  const isExpanded = expandedSections[section.id];
                  const isSectionActive = isPathInSection(section.id);

                  return (
                    <div key={section.id}>
                      {/* Section Header */}
                      <button
                        onClick={() => !section.comingSoon && toggleSection(section.id)}
                        disabled={section.comingSoon}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all active:scale-[0.98] ${
                          isSectionActive && !isExpanded
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                        } ${section.comingSoon ? 'opacity-50' : ''}`}
                      >
                        <SectionIcon className="w-5 h-5" />
                        <span className="flex-1 text-left font-semibold text-sm">{section.label}</span>
                        {section.comingSoon ? (
                          <span className="text-[10px] bg-yellow-500 text-white px-1.5 py-0.5 rounded font-medium">
                            Soon
                          </span>
                        ) : isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </button>

                      {/* Section Items - Expandable */}
                      {isExpanded && !section.comingSoon && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                          {section.items.map((item, idx) => {
                            // Sub-group header the first time a new group label
                            // appears, exactly as the sidebar does it — the long
                            // sections are unreadable on a phone without them.
                            const showGroupHeader =
                              !!item.group && item.group !== section.items[idx - 1]?.group;
                            return (
                              <React.Fragment key={item.path}>
                                {showGroupHeader && (
                                  <p className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                    {item.group}
                                  </p>
                                )}
                                {renderRow(item, true)}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Utility Items */}
          {visibleUtilityItems.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                Settings
              </p>
              <div className="space-y-1">
                {visibleUtilityItems.map((item) => renderRow(item, false))}
              </div>
            </div>
          )}

          {/* Divider */}
          <div className="mx-4 border-t border-gray-100 dark:border-gray-800" />

          {/* User Info & Logout */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-2xl mb-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg"
                style={{ background: 'var(--coral-tint)', color: 'var(--coral-label)' }}>
                {user?.firstName?.charAt(0).toUpperCase() || user?.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                  {(currentOrganization?.role || user?.role)?.replace(/_/g, ' ') || 'No role'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="tl-btn tl-btn-danger w-full py-3.5 active:scale-[0.98]"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-40 lg:hidden safe-area-bottom">
        <div className="flex items-center h-16 max-w-lg mx-auto">
          {visibleTabs.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all active:scale-95 ${
                  active
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <div className={`p-1.5 rounded-xl transition-colors ${active ? 'bg-blue-100 dark:bg-blue-900/40' : ''}`}>
                  <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : 'stroke-[1.5]'}`} />
                </div>
                <span className={`text-[10px] ${active ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* More Button */}
          <button
            onClick={() => setIsMoreMenuOpen(true)}
            className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-all active:scale-95 ${
              isMoreMenuOpen
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            <div className={`p-1.5 rounded-xl transition-colors ${isMoreMenuOpen ? 'bg-blue-100 dark:bg-blue-900/40' : ''}`}>
              <MoreHorizontal className={`w-5 h-5 ${isMoreMenuOpen ? 'stroke-[2.5]' : 'stroke-[1.5]'}`} />
            </div>
            <span className={`text-[10px] ${isMoreMenuOpen ? 'font-bold' : 'font-medium'}`}>
              More
            </span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
